// components/YouTubePlayer.jsx
import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";

const loadYTApi = (() => {
  let promise;
  return () => {
    if (promise) return promise;
    promise = new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve(window.YT);
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
      window.onYouTubeIframeAPIReady = () => resolve(window.YT);
    });
    return promise;
  };
})();

const YouTubePlayer = forwardRef(function YouTubePlayer(
  {
    videoId, // e.g. "rLm_aSP369M"
    width = "100%",
    height = "100%",
    autoplay = 0, // 1 or 0
    controls = 1, // native controls on/off
    playsinline = 1,
    muted = 0,
    start = 0,
    loop = 0,
    cc = 0, // captions default: 1 to force on
    modestbranding = 1,
    rel = 0, // related videos from same channel
    showinfo = 0, // legacy param; still accepted by some embeds
    onReady,
    onStateChange,
    onError,
  },
  ref
) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    play: () => playerRef.current?.playVideo(),
    pause: () => playerRef.current?.pauseVideo(),
    mute: () => playerRef.current?.mute(),
    unMute: () => playerRef.current?.unMute(),
    isMuted: () => playerRef.current?.isMuted(),
    setVolume: (v) =>
      playerRef.current?.setVolume(Math.max(0, Math.min(100, v))),
    getVolume: () => playerRef.current?.getVolume(),
    seekTo: (sec) => playerRef.current?.seekTo(Math.max(0, sec), true),
    getCurrentTime: () => playerRef.current?.getCurrentTime(),
    getDuration: () => playerRef.current?.getDuration(),
    setPlaybackRate: (rate) => playerRef.current?.setPlaybackRate(rate),
    getAvailablePlaybackRates: () =>
      playerRef.current?.getAvailablePlaybackRates?.() || [],
    setQuality: (q) => playerRef.current?.setPlaybackQuality?.(q), // e.g. 'hd1080','hd720','large','medium','small','auto'
    getPlayer: () => playerRef.current,
  }));

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const YT = await loadYTApi();
      if (!isMounted || !containerRef.current) return;

      playerRef.current = new YT.Player(containerRef.current, {
        width,
        height,
        videoId,
        playerVars: {
          autoplay,
          controls,
          playsinline,
          mute: muted,
          start,
          loop,
          cc_load_policy: cc ? 1 : 0,
          modestbranding,
          rel,
          // important for postMessage security
          origin: window.location.origin,
          // nice-to-have flags:
          enablejsapi: 1,
          iv_load_policy: 3, // hide annotations
          fs: 1, // fullscreen button
        },
        events: {
          onReady: (ev) => onReady?.(ev),
          onStateChange: (ev) => onStateChange?.(ev),
          onError: (ev) => onError?.(ev),
        },
      });
    })();

    return () => {
      isMounted = false;
      try {
        playerRef.current?.destroy?.();
      } catch {}
      playerRef.current = null;
    };
  }, [videoId, width, height, autoplay, controls, playsinline, muted, start, loop, cc, modestbranding, rel, onReady, onStateChange, onError]);

  return (
    <div style={{ position: "relative", width, height }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
});

export default YouTubePlayer;
